import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { CustomModal, Spinner, Button, StaticSelect, TextInput, showToast } from "../shared";
import { formatDistanceToNow } from "date-fns";
import { I18nProvider } from "@react-aria/i18n";
import { DatePicker } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { extractBackendErrorMessage, getFullName, toIsoDate, toUsDate } from "../../utils/utils";
import { useTaskDetails, useTaskComments, useCreateTaskComment, useUpdateTaskStatus } from "../../lib/react-query/tasks";
import { useProjectMembers } from "../../lib/react-query/projects";
import { SendIcon } from "../../assets/icons";
import { HiOutlinePaperClip, HiOutlineTrash, HiOutlineDocument, HiOutlineEye, HiOutlineArrowDownTray, HiOutlineXMark } from "react-icons/hi2";
import { STATUS_OPTIONS } from "../../constants/statuses";
import { TASK_COLORS } from "../../constants/taskColors";
import TaskAttachmentSection from "../Files/TaskAttachmentSection";
import FilePickerModal from "../Files/FilePickerModal";
import FileUploadAndAttachModal from "../Files/FileUploadAndAttachModal";
import { useAttachProjectFile, useDetachFileAttachment, useUploadAndAttachProjectFile } from "../../lib/react-query/fileAttachments";
import { useFileUrl, useFolderTree, usePresignUpload } from "../../lib/react-query/files";
import { useTaskModalStore } from "../../stores/taskModalStore";
import { FileViewer } from "../Files/FileViewer";
import { cn } from "../../lib/cn";
import { endPoints } from "../../redux/network/constant";

const PRIORITY_OPTIONS = [
  { id: "LOW", name: "Low" },
  { id: "MEDIUM", name: "Medium" },
  { id: "HIGH", name: "High" },
];

const getOptionName = (options, value) => (
  options.find((option) => String(option.id) === String(value))?.name || "Unknown"
);

// Memoized helper components
const TagPill = memo(function TagPill({ label, removable = false, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary border border-primary/10 transition-colors hover:bg-primary/10">
      {label}
      {removable ? (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          className="ml-0.5 rounded-full p-0.5 text-primary/60 hover:text-primary hover:bg-primary/20 transition-all focus:outline-none focus:ring-2 focus:ring-primary/40"
          onClick={onRemove}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      ) : null}
    </span>
  );
});

const AssigneeChip = memo(function AssigneeChip({ label, isActive, isAssigned, onToggle }) {
  return (
    <button
      type="button"
      disabled={!isActive}
      onClick={isActive ? onToggle : undefined}
      className={`
        inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-200
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
        ${isAssigned 
          ? "border-primary bg-primary/5 text-primary shadow-sm shadow-primary/10" 
          : "border-surface-border bg-white text-grey hover:border-surface-border hover:bg-lightgrey hover:text-darkgrey"}
        ${isActive ? "cursor-pointer" : "cursor-not-allowed opacity-50 grayscale"}
      `}
    >
      <div className={`w-2 h-2 rounded-full ${isAssigned ? "bg-primary" : "bg-grey"}`} />
      {label}
      {!isActive ? <span className="ml-1 text-[10px] uppercase tracking-wider font-bold text-grey">Inactive</span> : null}
    </button>
  );
});

const CommentAttachmentSummary = ({ count, label, isExpanded, onToggle, children, actions }) => {
  if (count === 0) return null;
  const text = label || (count === 1 ? "1 file attached" : `${count} files attached`);

  return (
    <div className="mt-2">
      <div className={cn("flex items-center", actions ? "justify-between" : "")}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-dark hover:text-darkgrey transition-colors rounded-md px-1.5 py-0.5 -ml-1.5"
        >
          <HiOutlinePaperClip className="h-3.5 w-3.5 shrink-0" />
          <span>{text}</span>
          <svg
            className={cn("h-3 w-3 shrink-0 transition-transform duration-150", isExpanded && "rotate-180")}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>
        {actions && (
          <div className="flex items-center gap-1.5">
            {actions}
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="mt-1.5 flex flex-col gap-1">
          {children}
        </div>
      )}
    </div>
  );
};

const UpdateTaskModal = memo(function UpdateTaskModal({ isOpen, onClose, projectId, taskId, initialTask, readOnly = false }) {
  const { data: fetchedTask, isLoading: loadingTask, isError: taskError } = useTaskDetails(isOpen ? projectId : null, isOpen ? taskId : null, isOpen);
  const task = fetchedTask ?? initialTask;

  const { data: commentsData, isLoading: loadingComments, isError: commentsError } = useTaskComments(isOpen ? projectId : null, isOpen ? taskId : null, isOpen);

  const { mutateAsync: createComment, isPending: postingComment } = useCreateTaskComment();
  const { mutateAsync: updateStatus, isPending: savingStatus } = useUpdateTaskStatus();
  const { mutateAsync: attachProjectFile, isPending: attachingFile } = useAttachProjectFile();
  const { mutateAsync: detachAttachment } = useDetachFileAttachment();
  const { mutateAsync: getFileUrl } = useFileUrl();
  const presignUpload = usePresignUpload();
  const uploadAndAttach = useUploadAndAttachProjectFile();
  const { data: folderTreeData } = useFolderTree(projectId, null, isOpen);

  const expandedCommentIds = useTaskModalStore((s) => s.expandedCommentIds);
  const draftAttachmentsExpanded = useTaskModalStore((s) => s.draftAttachmentsExpanded);
  const draftExistingAttachments = useTaskModalStore((s) => s.draftExistingAttachments);
  const draftUploadAttachments = useTaskModalStore((s) => s.draftUploadAttachments);
  const newComment = useTaskModalStore((s) => s.newComment);
  const pickerTarget = useTaskModalStore((s) => s.pickerTarget);
  const selectedFileId = useTaskModalStore((s) => s.selectedFileId);
  const selectedPickerFile = useTaskModalStore((s) => s.selectedPickerFile);
  const uploadTarget = useTaskModalStore((s) => s.uploadTarget);
  const toggleCommentAttachments = useTaskModalStore((s) => s.toggleCommentAttachments);
  const toggleDraftAttachments = useTaskModalStore((s) => s.toggleDraftAttachments);
  const addDraftExistingAttachment = useTaskModalStore((s) => s.addDraftExistingAttachment);
  const addDraftUploadAttachments = useTaskModalStore((s) => s.addDraftUploadAttachments);
  const removeDraftAttachment = useTaskModalStore((s) => s.removeDraftAttachment);
  const clearDraftAttachments = useTaskModalStore((s) => s.clearDraftAttachments);
  const setNewComment = useTaskModalStore((s) => s.setNewComment);
  const openPicker = useTaskModalStore((s) => s.openPicker);
  const closePicker = useTaskModalStore((s) => s.closePicker);
  const setSelectedFile = useTaskModalStore((s) => s.setSelectedFile);
  const openUpload = useTaskModalStore((s) => s.openUpload);
  const closeUpload = useTaskModalStore((s) => s.closeUpload);
  const resetTaskModalStore = useTaskModalStore((s) => s.reset);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    status: "",
    priority: "",
    start_date: "",
    due_date: "",
    color: "",
    tags: [],
    assignees: [],
  });
  const [newTag, setNewTag] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [viewerFile, setViewerFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const startIso = toIsoDate(editForm.start_date);
  const dueIso = toIsoDate(editForm.due_date);
  const activeTaskKey = `${projectId ?? "project"}:${taskId ?? "task"}`;

  // Memoized derived data
  const comments = useMemo(() => 
    Array.isArray(commentsData?.items) ? commentsData.items : Array.isArray(commentsData) ? commentsData : [],
    [commentsData]
  );

  const { data: members, isLoading: loadingMembers } = useProjectMembers(projectId, !!projectId);

  const assignedSet = useMemo(() => new Set((editForm.assignees || []).map((x) => String(x))), [editForm.assignees]);

  // Reset state on close
  useEffect(() => {
    if (!isOpen) {
      resetTaskModalStore();
      setNewTag("");
      setAssigneeSearch("");
    }
  }, [isOpen, resetTaskModalStore]);

  useEffect(() => {
    if (!isOpen) return;
    resetTaskModalStore();
    setNewTag("");
    setAssigneeSearch("");
  }, [activeTaskKey, isOpen, resetTaskModalStore]);

  // Populate form when task loads
  useEffect(() => {
    if (task) {
      setEditForm({
        title: task?.title || "",
        description: task?.description || "",
        status: task?.status || "PENDING",
        priority: task?.priority || "MEDIUM",
        start_date: task?.start_date || "",
        due_date: task?.due_date || "",
        color: task?.color || "",
        tags: Array.isArray(task?.tags) ? task.tags : [],
        assignees: Array.isArray(task?.assignees)
          ? task.assignees.map((x) => String(x?.id ?? x))
          : [],
      });
    }
  }, [task]);

  // Memoized handlers
  const handleFormChange = useCallback((e) => {
    setEditForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }, []);

  const handleStatusChange = useCallback((e) => {
    setEditForm((p) => ({ ...p, status: e.target.value }));
  }, []);

  const handlePriorityChange = useCallback((e) => {
    setEditForm((p) => ({ ...p, priority: e.target.value }));
  }, []);

  const handleStartDateChange = useCallback((val) => {
    setEditForm((p) => ({ ...p, start_date: val ? toUsDate(val.toString()) : "" }));
  }, []);

  const handleDueDateChange = useCallback((val) => {
    setEditForm((p) => ({ ...p, due_date: val ? toUsDate(val.toString()) : "" }));
  }, []);

  const handleAddTag = useCallback(() => {
    const v = newTag.trim();
    if (!v) return;
    setEditForm((p) => ({ ...p, tags: Array.from(new Set([...(p.tags || []), v])) }));
    setNewTag("");
  }, [newTag]);

  const handleRemoveTag = useCallback((tag) => {
    setEditForm((p) => ({ ...p, tags: p.tags.filter((t) => t !== tag) }));
  }, []);

  const handleToggleAssignee = useCallback((id, isAssigned) => {
    setEditForm((p) => ({
      ...p,
      assignees: isAssigned ? p.assignees.filter((x) => String(x) !== id) : [...p.assignees, id],
    }));
  }, []);

  const uploadFileToS3 = useCallback(async (upload, file) => {
    const formData = new FormData();
    Object.entries(upload.fields || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append("file", file);

    const response = await fetch(upload.upload_url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed for ${file.name}`);
    }
  }, []);

  const handlePreviewAttachment = useCallback(async (attachment) => {
    try {
      const isPdf = attachment.file?.extension?.toLowerCase()?.replace(/^\./, "") === "pdf";
      if (isPdf) {
        const url = await getFileUrl({ projectId, fileId: attachment.file.id });
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        return;
      }

      setViewerFile(attachment.file);
      setPreviewUrl(null);
      setPreviewError(null);
      const url = await getFileUrl({
        projectId,
        fileId: attachment.file.id,
      });
      if (url) {
        setPreviewUrl(url);
      }
    } catch (err) {
      setPreviewError(extractBackendErrorMessage(err, "Failed to load preview"));
      setPreviewUrl(null);
    }
  }, [getFileUrl, projectId]);

  const handleCloseViewer = useCallback(() => {
    setViewerFile(null);
    setPreviewUrl(null);
    setPreviewError(null);
  }, []);

  const handleDownloadAttachment = useCallback(async (attachment) => {
    try {
      const url = await getFileUrl({
        projectId,
        fileId: attachment.file.id,
      });
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.file.original_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      showToast("Failed to download file.", "error");
    }
  }, [getFileUrl, projectId]);

  const handleDetachCommentAttachment = useCallback(async (attachment) => {
    try {
      await detachAttachment({
        projectId,
        attachmentId: attachment.id,
      });
      showToast("File detached.", "success");
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to detach file"), "error");
    }
  }, [detachAttachment, projectId]);

  const handleAttachToComment = useCallback(async (commentId, fileId) => {
    await attachProjectFile({
      projectId,
      payload: {
        file_id: fileId,
        object_type: "TASK_COMMENT",
        object_id: commentId,
        role: "COMMENT_ATTACHMENT",
      },
    });
  }, [attachProjectFile, projectId]);

  const handleAttachSelection = useCallback(async (fileId) => {
    if (!pickerTarget) return;
    const selectedFile = selectedPickerFile;

    try {
      if (pickerTarget.kind === "draft") {
        if (selectedFile) {
          addDraftExistingAttachment({
            key: `existing-${selectedFile.id}`,
            fileId: selectedFile.id,
            name: selectedFile.name,
            meta: `${selectedFile.category || "FILE"}${selectedFile.size_bytes ? ` • ${(selectedFile.size_bytes / 1024).toFixed(1)} KB` : ""}`,
          });
        }
      } else if (pickerTarget.kind === "comment") {
        await handleAttachToComment(pickerTarget.commentId, fileId);
        showToast("File attached successfully.", "success");
      }
      closePicker();
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to attach file"), "error");
    }
  }, [handleAttachToComment, pickerTarget, selectedPickerFile, addDraftExistingAttachment, closePicker]);

  const handleUploadSelection = useCallback(async ({ files, folderId }) => {
    if (!uploadTarget || !files?.length) return;

    if (uploadTarget.kind === "draft") {
      addDraftUploadAttachments(
        files.map((file, index) => ({
          key: `draft-upload-${file.name}-${file.size}-${Date.now()}-${index}`,
          file,
          folderId: folderId ?? null,
          name: file.name,
          meta: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        }))
      );
      closeUpload();
      return;
    }

    try {
      const presigned = await presignUpload.mutateAsync({
        projectId,
        files: files.map((file) => ({
          original_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        })),
        folderId: folderId ?? null,
      });

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const upload = presigned?.files?.[index];
        if (!upload) {
          throw new Error(`Missing upload payload for ${file.name}`);
        }

        await uploadFileToS3(upload, file);
        await uploadAndAttach.mutateAsync({
          projectId,
          s3_key: upload.s3_key,
          original_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          folder_id: upload.folder_id ?? folderId ?? null,
          attach_to: {
            object_type: "TASK_COMMENT",
            object_id: Number(uploadTarget.commentId),
            role: "COMMENT_ATTACHMENT",
          },
        });
      }

      showToast("File uploaded and attached.", "success");
      closeUpload();
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to upload and attach file"), "error");
    }
  }, [presignUpload, projectId, uploadAndAttach, uploadFileToS3, uploadTarget, addDraftUploadAttachments, closeUpload]);

  const handlePostComment = useCallback(async () => {
    const trimmedComment = newComment.trim();
    if (!trimmedComment) return;
    try {
      const createdComment = await createComment({
        projectId,
        taskId,
        payload: { body: trimmedComment, message_type: "comment" },
      });

      const failures = [];

      for (const item of draftExistingAttachments) {
        try {
          await handleAttachToComment(createdComment.id, item.fileId);
        } catch (err) {
          failures.push(item.name);
        }
      }

      if (draftUploadAttachments.length) {
        try {
          const presigned = await presignUpload.mutateAsync({
            projectId,
            files: draftUploadAttachments.map((item) => ({
              original_name: item.file.name,
              content_type: item.file.type || "application/octet-stream",
              size_bytes: item.file.size,
            })),
            folderId: draftUploadAttachments[0]?.folderId ?? null,
          });

          for (let index = 0; index < draftUploadAttachments.length; index += 1) {
            const item = draftUploadAttachments[index];
            const upload = presigned?.files?.[index];
            if (!upload) {
              failures.push(item.name);
              continue;
            }

            try {
              await uploadFileToS3(upload, item.file);
              await uploadAndAttach.mutateAsync({
                projectId,
                s3_key: upload.s3_key,
                original_name: item.file.name,
                content_type: item.file.type || "application/octet-stream",
                size_bytes: item.file.size,
                folder_id: upload.folder_id ?? item.folderId ?? null,
                attach_to: {
                  object_type: "TASK_COMMENT",
                  object_id: Number(createdComment.id),
                  role: "COMMENT_ATTACHMENT",
                },
              });
            } catch (err) {
              failures.push(item.name);
            }
          }
        } catch (err) {
          draftUploadAttachments.forEach((item) => failures.push(item.name));
        }
      }

      setNewComment("");
      clearDraftAttachments();

      if (failures.length) {
        showToast(`Comment posted. Some files failed: ${failures.join(", ")}`, "error");
      } else {
        showToast("Comment posted.", "success");
      }
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to post comment"), "error");
    }
  }, [
    newComment,
    projectId,
    taskId,
    createComment,
    draftExistingAttachments,
    draftUploadAttachments,
    handleAttachToComment,
    presignUpload,
    uploadAndAttach,
    uploadFileToS3,
    clearDraftAttachments,
    setNewComment,
  ]);

  const handleSaveTask = useCallback(async () => {
    try {
      await updateStatus({ projectId, taskId, payload: editForm });
      showToast("Task updated", "success");
      onClose();
    } catch (err) {
      showToast(extractBackendErrorMessage(err, "Failed to update task"), "error");
    }
  }, [projectId, taskId, editForm, updateStatus, onClose]);

  // Filtered members
  const filteredMembers = useMemo(() => {
    if (!Array.isArray(members)) return [];
    const q = (assigneeSearch || "").toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const label = (getFullName(m?.user) || m?.user?.email || String(m?.user?.id ?? m.user)).toLowerCase();
      return label.includes(q);
    });
  }, [members, assigneeSearch]);

  return (
    <CustomModal
      title={readOnly ? "Task Details" : "Edit Task"}
      open={isOpen}
      close={onClose}
      showPrimaryButton={!readOnly && !loadingTask && !taskError}
      primaryButtonText="Save Changes"
      primaryButtonClick={handleSaveTask}
      primaryButtonLoading={savingStatus}
      primaryButtonDisabled={savingStatus}
      showSecondaryButton={true}
      secondaryButtonText="Close"
      secondaryButtonClick={onClose}
      dialogSize="2xl"
    >
      {loadingTask ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      ) : taskError ? (
        <div className="p-8 text-center">
          <div className="w-12 h-12 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-danger" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-darkgrey font-medium">Failed to load task details</p>
        </div>
      ) : (
        /* ========== EDIT TASK ========== */
        <div className="animate-fade-in">
          <div className="p-6">
            <div className="space-y-6">
              {/* Core Fields */}
              <div className="space-y-3">
                <TextInput
                  label="Title"
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={handleFormChange}
                  placeholder="Enter task title"
                  disabled={readOnly}
                />
                
                <div className="grid gap-3 sm:grid-cols-3">
                  {readOnly ? (
                    <div className="flex min-h-12 flex-col justify-center rounded-lg border border-surface-border bg-lightgrey px-3">
                      <span className="text-xs text-grey">Status</span>
                      <span className="text-sm text-darkgrey">
                        {getOptionName(STATUS_OPTIONS, editForm.status)}
                      </span>
                    </div>
                  ) : (
                    <StaticSelect
                      options={STATUS_OPTIONS}
                      label="Status"
                      name="status"
                      value={editForm.status}
                      onChange={handleStatusChange}
                    />
                  )}
                  {readOnly ? (
                    <div className="flex min-h-12 flex-col justify-center rounded-lg border border-surface-border bg-lightgrey px-3">
                      <span className="text-xs text-grey">Priority</span>
                      <span className="text-sm text-darkgrey">
                        {getOptionName(PRIORITY_OPTIONS, editForm.priority)}
                      </span>
                    </div>
                  ) : (
                    <StaticSelect
                      options={PRIORITY_OPTIONS}
                      label="Priority"
                      name="priority"
                      value={editForm.priority}
                      onChange={handlePriorityChange}
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <I18nProvider locale="en-US">
                      <DatePicker
                        label="Start Date"
                        name="start_date"
                        disableAnimation
                        granularity="day"
                        value={startIso ? parseDate(startIso) : null}
                        onChange={handleStartDateChange}
                        isDisabled={readOnly}
                        showMonthAndYearPickers
                      />
                    </I18nProvider>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <I18nProvider locale="en-US">
                      <DatePicker
                        label="Due Date"
                        name="due_date"
                        disableAnimation
                        granularity="day"
                        value={dueIso ? parseDate(dueIso) : null}
                        onChange={handleDueDateChange}
                        isDisabled={readOnly}
                        showMonthAndYearPickers
                      />
                    </I18nProvider>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-grey pb-1">Color</p>
                    <div className="flex items-center gap-2">
                      {TASK_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          aria-label={`Task color ${c}`}
                          className={`w-7 h-7 rounded-full cursor-pointer transition ${editForm.color === c ? "ring-2 ring-offset-2 ring-secondary" : "hover:scale-110"}`}
                          style={{ backgroundColor: c }}
                          onClick={readOnly ? undefined : () => setEditForm((p) => ({ ...p, color: p.color === c ? "" : c }))}
                          disabled={readOnly}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <TextInput
                  label="Description"
                  type="text"
                  name="description"
                  value={editForm.description}
                  onChange={handleFormChange}
                  placeholder="Add a detailed description..."
                  disabled={readOnly}
                />
              </div>

              {/* Separator */}
              <div className="border-t border-surface-border" />

              {/* Assignees Section */}
              {!readOnly && <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-darkgrey">Assignees</h4>
                    <p className="text-xs text-grey">Manage team members assigned to this task</p>
                  </div>
                  <span className="text-xs text-grey font-medium">{assignedSet.size} selected</span>
                </div>
                <TextInput
                  label=""
                  type="text"
                  name="assigneeSearch"
                  value={assigneeSearch}
                  onChange={(e) => setAssigneeSearch(e.target.value)}
                  placeholder="Search members..."
                />
                <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
                  {filteredMembers.map((m) => {
                    const id = String(m?.user?.id ?? m.user);
                    const label = getFullName(m?.user) || m?.user?.email || id;
                    const isAssigned = assignedSet.has(id);
                    const isActive = m.status === "ACTIVE";
                    return (
                      <AssigneeChip 
                        key={id} 
                        label={label} 
                        isActive={isActive} 
                        isAssigned={isAssigned} 
                        onToggle={() => handleToggleAssignee(id, isAssigned)} 
                      />
                    );
                  })}
                  {loadingMembers && <Spinner size="sm" />}
                  {!loadingMembers && filteredMembers.length === 0 && (
                    <p className="text-sm text-grey italic py-2">No members found.</p>
                  )}
                </div>
              </div>}

              {/* Tags Section */}
              {!readOnly && <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-darkgrey">Tags</h4>
                  <p className="text-xs text-grey">Categorize this task</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <TextInput
                      label=""
                      type="text"
                      name="new_tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Add tag (press Enter)"
                    />
                  </div>
                  <Button
                    onClick={handleAddTag}
                    className="h-[42px] px-4 rounded-lg shrink-0"
                    variant="outlined"
                    color="secondary"
                    size="md"
                  >
                    Add
                  </Button>
                </div>
                {(editForm.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {(editForm.tags || []).map((tag) => (
                      <TagPill key={tag} label={tag} removable onRemove={() => handleRemoveTag(tag)} />
                    ))}
                  </div>
                )}
              </div>}
            </div>

            {/* File Attachments */}
            <div className="border-t border-surface-border mt-6">
              <div className="p-2">
                <TaskAttachmentSection
                  projectId={projectId}
                  taskId={taskId}
                />
              </div>
            </div>
          </div>

          {/* Discussion Section */}
          <div className="border-t border-surface-border">
            <div className="p-6 pt-4">
              <h4 className="text-sm font-semibold text-darkgrey mb-4">Discussion</h4>
              
              {/* Comments */}
              <div className="max-h-[180px] overflow-y-auto custom-scrollbar space-y-3 mb-4">
                {loadingComments ? (
                  <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                ) : commentsError ? (
                  <p className="text-sm text-danger text-center py-4">Failed to load comments</p>
                ) : comments.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-grey">No comments yet. Start the discussion below.</p>
                  </div>
                ) : (
                  comments.map((c) => {
                    const commentAttachments = c.attachments || [];
                    const isExpanded = expandedCommentIds.has(c.id);
                    const commentPermissions = c.permissions || {};
                    const canAttachToComment = commentPermissions.can_attach === true;
                    const canDetachFromComment = commentPermissions.can_detach === true;
                    const commentActions = canAttachToComment ? (
                      <>
                        <button
                          type="button"
                          onClick={() => openPicker({ kind: "comment", commentId: c.id })}
                          className="rounded-lg px-2 py-1 text-[11px] font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
                        >
                          Attach file
                        </button>
                        <button
                          type="button"
                          onClick={() => openUpload({ kind: "comment", commentId: c.id })}
                          className="rounded-lg px-2 py-1 text-[11px] font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
                        >
                          Upload & attach
                        </button>
                      </>
                    ) : null;

                    return (
                      <div key={c.id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary/10 text-secondary-dark flex items-center justify-center text-xs font-bold shrink-0">
                          {(c.author_name || c.author || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-white border border-surface-border rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-semibold text-darkgrey">{c.author_name || c.author}</span>
                              <span className="text-[10px] text-grey">
                                {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm text-darkgrey leading-relaxed">{c.body}</p>
                            <CommentAttachmentSummary
                              count={commentAttachments.length}
                              isExpanded={isExpanded}
                              onToggle={() => toggleCommentAttachments(c.id)}
                              actions={commentActions}
                            >
                              {commentAttachments.map((att) => {
                                const isUnavailable = att.status === "UNAVAILABLE";
                                return (
                                  <div
                                    key={att.id}
                                    className="flex items-center gap-2 rounded-md border border-surface-border bg-white px-2.5 py-1.5"
                                  >
                                    <HiOutlineDocument className="h-3.5 w-3.5 shrink-0 text-grey" />
                                    <span className={cn("truncate text-xs", isUnavailable ? "text-grey line-through" : "text-darkgrey")}>
                                      {att.file?.original_name || "Unknown file"}
                                    </span>
                                    <div className="ml-auto flex items-center gap-0.5 shrink-0">
                                      {!isUnavailable && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => handlePreviewAttachment(att)}
                                            className="rounded p-1 text-grey hover:bg-surface-hover hover:text-darkgrey"
                                            aria-label="Preview"
                                          >
                                            <HiOutlineEye className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDownloadAttachment(att)}
                                            className="rounded p-1 text-grey hover:bg-surface-hover hover:text-darkgrey"
                                            aria-label="Download"
                                          >
                                            <HiOutlineArrowDownTray className="h-3.5 w-3.5" />
                                          </button>
                                        </>
                                      )}
                                      {canDetachFromComment && (
                                        <button
                                          type="button"
                                          onClick={() => handleDetachCommentAttachment(att)}
                                          className="rounded p-1 text-grey hover:bg-surface-hover hover:text-red"
                                          aria-label="Detach"
                                        >
                                          <HiOutlineXMark className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {isExpanded && commentAttachments.some((a) => a.status === "UNAVAILABLE") && (
                                <p className="text-[11px] text-grey italic">Some files are no longer available.</p>
                              )}
                            </CommentAttachmentSummary>
                            {commentAttachments.length === 0 && commentActions ? (
                              <div className="mt-2 flex items-center gap-1.5">
                                {commentActions}
                              </div>
                            ) : null}
                          </div>

                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Comment Input */}
              {!readOnly && <div className="space-y-2.5">
                {(draftExistingAttachments.length > 0 || draftUploadAttachments.length > 0) && (
                  <CommentAttachmentSummary
                    count={draftExistingAttachments.length + draftUploadAttachments.length}
                    label={
                      draftExistingAttachments.length + draftUploadAttachments.length === 1
                        ? "1 file ready"
                        : `${draftExistingAttachments.length + draftUploadAttachments.length} files ready`
                    }
                    isExpanded={draftAttachmentsExpanded}
                    onToggle={toggleDraftAttachments}
                  >
                    {[...draftExistingAttachments, ...draftUploadAttachments].map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center gap-2 rounded-md border border-surface-border bg-white px-2.5 py-1.5"
                      >
                        <HiOutlinePaperClip className="h-3.5 w-3.5 shrink-0 text-grey" />
                        <span className="truncate text-xs text-darkgrey">{item.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDraftAttachment(item.key)}
                          className="ml-auto rounded p-1 text-grey hover:bg-surface-hover hover:text-danger shrink-0"
                          aria-label={`Remove ${item.name}`}
                        >
                          <HiOutlineTrash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </CommentAttachmentSummary>
                )}

                <div className="relative">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handlePostComment();
                      }
                    }}
                    className="w-full rounded-lg bg-lightgrey border border-surface-border pl-4 pr-11 py-2.5 text-sm text-darkgrey placeholder:text-grey focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    placeholder="Write a comment..."
                    disabled={postingComment}
                  />
                  <button
                    type="button"
                    aria-label="Send comment"
                    disabled={postingComment || !newComment.trim()}
                    onClick={handlePostComment}
                    className={cn(
                      "absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded transition-colors",
                      postingComment || !newComment.trim()
                        ? "cursor-not-allowed text-grey opacity-40"
                        : "text-primary hover:bg-primary/10",
                    )}
                  >
                    {postingComment ? <Spinner size="sm" className="text-current" /> : <SendIcon className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => openPicker({ kind: "draft" })}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
                  >
                    Attach file
                  </button>
                  <button
                    type="button"
                    onClick={() => openUpload({ kind: "draft" })}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-secondary-dark transition-colors hover:bg-secondary/10"
                  >
                    Upload & attach
                  </button>
                </div>
              </div>}
            </div>
          </div>
        </div>
      )}

      <FilePickerModal
        open={!!pickerTarget}
        onClose={closePicker}
        tree={folderTreeData?.tree}
        selectedFileId={selectedFileId}
        onSelectFile={(file) => setSelectedFile(file.id, file)}
        onAttach={handleAttachSelection}
        isLoading={attachingFile}
        title={pickerTarget?.kind === "comment" ? "Attach file to comment" : "Add file to comment draft"}
        attachLabel={pickerTarget?.kind === "comment" ? "Attach file" : "Add file"}
      />

      <FileUploadAndAttachModal
        open={!!uploadTarget}
        onClose={closeUpload}
        onUpload={handleUploadSelection}
        isLoading={presignUpload.isPending || uploadAndAttach.isPending}
        objectType={uploadTarget?.kind === "comment" ? "comment" : "comment draft"}
        folderTree={folderTreeData?.tree}
      />

      <FileViewer
        file={viewerFile}
        previewUrl={previewUrl}
        contentUrl={viewerFile ? endPoints.ProjectFileContent(projectId, viewerFile.id) : undefined}
        previewError={previewError}
        isOpen={!!viewerFile}
        onClose={handleCloseViewer}
        onDownload={async () => {
          if (!viewerFile) return;
          try {
            const url = await getFileUrl({ projectId, fileId: viewerFile.id });
            if (url) {
              const a = document.createElement("a");
              a.href = url;
              a.download = viewerFile.original_name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          } catch (err) {
            showToast("Failed to download file.", "error");
          }
        }}
        canShare={false}
      />
    </CustomModal>
  );
});

export default UpdateTaskModal;
